import { Client } from "@/Common/Api/Client";
import { BaseReactComponent, MutateState } from "@/Common/Components/BaseReactComponent";
import { PrintHelper } from "@/Common/Helpers/PrintHelper";
import { FormModificationState } from "@/Common/Models/UI/Forms/FormModificationState";
import { TableDataSourceItem } from "@/Common/Models/UI/Tables/ITableDataSourceItem";
import * as LocalPatientOperations from "@/Common/Operations/operations.patient.local";
import * as ServerOperations from "@/Common/Operations/operations.server";
import { MedicalItemImmunization } from "@/Patient/Models/MedicalSummary/MedicalItemImmunization";
import { ConditionCategory } from "./Constants/ConditionCategory";
import { ConstantValues } from "./Constants/ConstantValues";
import { AntigenCommonComponent } from "./ImmunizationProfileSections/AntigenCommonComponent";
import { ImmunizationDeletion } from "./ImmunizationProfileSections/ImmunizationDeletion";
import { ImmunizationPreferences } from "./ImmunizationProfileSections/ImmunizationPreferences";
import { ImmunizationRefusal } from "./ImmunizationProfileSections/ImmunizationRefusal";
import { VaccinationOverrideStatusComponent } from "./ImmunizationProfileSections/VaccinationOverrideStatusComponent";
import { ViewImmunization } from "./ImmunizationProfileSections/ViewImmunization";
import { ContraindicationsPrecautionsTable } from "./ImmunizationProfileTables/ContraindicationsPrecautionsTable";
import { ExpectedImmunizationTable } from "./ImmunizationProfileTables/ExpectedImmunizationTable";
import { ImmunityProofTable } from "./ImmunizationProfileTables/ImmunityProofTable";
import { ImmunizationHistoryTable } from "./ImmunizationProfileTables/ImmunizationHistoryTable";
import { ImmunizationRefusalTable } from "./ImmunizationProfileTables/ImmunizationRefusalTable";
import { NonValidImmunizationsSummaryTable } from "./ImmunizationProfileTables/NonValidImmunizationsSummaryTable";
import { ConditionModel } from "./Models/ConditionModel";
import { ImmunizationModel } from "./Models/ImmunizationModel";
import { ImmunizationProfileModel } from "./Models/ImmunizationProfileModel";
import { RvqLocationPreference } from "./Models/RvqLocationPreference";
import { RvqMedicationPreference } from "./Models/RvqMedicationPreference";
import { RvqPractitionerPreference } from "./Models/RvqPractitionerPreference";
import { VaccinationRefusalModel } from "./Models/VaccinationRefusalModel";
import { GenerateImmunizationProfilePdfResponse } from "./ResponseModels/GenerateImmunizationprofilePdfResponse";
import { ImmunizationProfileInteractionPopupType, ImmunizationProfileProps, ImmunizationProfileState } from "./ImmunizationProfileController";
import { QuebecVaccinationRegistryHelper } from "./QuebecVaccinationRegistryHelper";

import img_loading from "@/Globals/_images/loading.gif";

interface ImmunizationProfileComponentProps extends ImmunizationProfileProps {
    parentState: ImmunizationProfileState;
    mutateParentState: MutateState<ImmunizationProfileState>;
    immunizationProfileModel: ImmunizationProfileModel;
    updateImmunizationProfileModel?: (model: ImmunizationProfileModel, appliedLocation?: RvqLocationPreference) => void;
}

class ImmunizationProfileComponent extends BaseReactComponent<ImmunizationProfileComponentProps, null> {
    public componentDidMount(): void {
        super.setOverlay("ImmunizationProfileComponent");
    }

    public render(): JSX.Element {
        const state = this.props.parentState;
        const immunizationProfileModel = this.props.immunizationProfileModel;
        let immunityProofs = (immunizationProfileModel?.Conditions || [])
            .filter(i => ConditionCategory[i.Category?.Code] == ConditionCategory.immunizationProof);
        let contraindications = (immunizationProfileModel?.Conditions || [])
            .filter(i => ConditionCategory[i.Category?.Code] == ConditionCategory.contraindicationPrecaution);

        const recommendations = (immunizationProfileModel?.ImmunizationRecommendations || [])
            .flatten(i => i.Recommendations);

        return (
            <div>
                {this.renderPopup()}
                <div className={"immunization-profile-popup " + (this.hasInnerPopup ? "behind-global-overlay" : "")}>
                    <div className="popup-header">
                        <div className="flex-container" style={{ justifyContent: "space-between" }}>
                            <h2>{cacheStore.GetString("QuebecVaccinationRegistry")}</h2>
                            <div className="pointer popup-attachment-header-right-btnClose" onClick={() => this.props.onClose()}>X</div>
                        </div>
                    </div>

                    <div className="popup-body">
                        {this.renderRow(<a onClick={() => this.props.mutateParentState(s => s.currentPopup = ImmunizationProfileInteractionPopupType.Preferences)}>{cacheStore.GetString("QuebecVaccinationRegistry_PreferencesLabel")}</a>)}
                        {state.messages?.any() &&
                            this.renderRow(<div style={{ backgroundColor: "#ecced5", width: "100%", border: "1px solid #a31638", padding: "10px" }}>{state.messages.join("\n")}</div>)
                        }
                        {this.renderRow(
                            <ImmunizationHistoryTable
                                isReadOnly={state.isProfileReadOnly}
                                onChangeStatusClick={model => this.openHistoryVacinationModelPopup(model, ImmunizationProfileInteractionPopupType.OverrideVaccinationStatus, FormModificationState.Editing)}
                                onDeleteClick={model => this.openHistoryVacinationModelPopup(model, ImmunizationProfileInteractionPopupType.ImmunityDeletion, FormModificationState.Editing)}
                                onViewClick={model => this.openHistoryVacinationModelPopup(model, ImmunizationProfileInteractionPopupType.ViewVaccination, FormModificationState.Viewing)}
                                dataSource={this.toDataSource(immunizationProfileModel?.Immunizations.sortByPropertyDesc(x => x.Date))}
                                isCorrector={state.isCorrector}
                                immunizationDeletePermissions={this.props.parentState.immunizationDeletePermissions}
                            />
                        )}
                        {this.renderRow(
                            <ExpectedImmunizationTable
                                isReadOnly={state.isProfileReadOnly}
                                dataSource={this.toDataSource(
                                    recommendations
                                        .sortByProperty(
                                            recommendations => recommendations.DateCriteria[1]?.Date
                                        )
                                )}
                            />
                        )}
                        <div className="col-md-12" style={{ marginBottom: "20px" }}>
                            {this.renderPlannedVaccinationStatus("QuebecVaccinationRegistry_Values_ForecastStatus_Admissible", "QuebecVaccinationRegistry_EligibleStatusDescription")}
                            {this.renderPlannedVaccinationStatus("QuebecVaccinationRegistry_Values_ForecastStatus_Échéance", "QuebecVaccinationRegistry_PlannedStatusDescription")}
                            {this.renderPlannedVaccinationStatus("QuebecVaccinationRegistry_Values_ForecastStatus_Retard", "QuebecVaccinationRegistry_LateStatusDescription")}
                            {this.renderPlannedVaccinationStatus("QuebecVaccinationRegistry_Values_ForecastStatus_À jour", "QuebecVaccinationRegistry_UpToDateStatusDescription")}
                        </div>
                        {this.renderRow(
                            <NonValidImmunizationsSummaryTable
                                isReadOnly={state.isProfileReadOnly}
                                dataSource={this.toDataSource(
                                    immunizationProfileModel?.Immunizations
                                        .filter(i => ConstantValues.InvalidStatuses.contains(i.OverrideStatus.Code))
                                        .sortByPropertyDesc(x => x.Date)
                                )}
                            />
                        )}
                        {this.renderRow(
                            <ContraindicationsPrecautionsTable
                                isReadOnly={state.isProfileReadOnly}
                                dataSource={this.toDataSource(
                                    [
                                        ...contraindications
                                            .filter(x => !x.DatePeriod?.End)
                                            .sortByPropertyDesc(x => x.DatePeriod?.Start),
                                        ...contraindications
                                            .filter(x => !!x.DatePeriod?.End)
                                            .sortByPropertyDesc(x => x.DatePeriod?.End)
                                    ]
                                )}
                                onAdd={() => this.props.mutateParentState(s => {
                                    s.currentPopup = ImmunizationProfileInteractionPopupType.CounterindicationOrPrecaution;
                                    s.formMode = FormModificationState.Creating;
                                    s.currentCondition = null;
                                })}
                                onActionClick={(item, isReadOnly) => this.props.mutateParentState(s => {
                                    s.currentCondition = item;
                                    s.currentPopup = ImmunizationProfileInteractionPopupType.CounterindicationOrPrecaution;
                                    s.formMode = isReadOnly ? FormModificationState.Viewing : FormModificationState.Editing;
                                })}
                            />
                        )}
                        {this.renderRow(
                            <ImmunityProofTable
                                isReadOnly={state.isProfileReadOnly}
                                dataSource={this.toDataSource(
                                    [
                                        ...immunityProofs
                                            .filter(x => !x.DatePeriod?.End)
                                            .sortByPropertyDesc(x => x.DatePeriod?.Start),
                                        ...immunityProofs
                                            .filter(x => !!x.DatePeriod?.End)
                                            .sortByPropertyDesc(x => x.DatePeriod?.End)
                                    ]
                                )}
                                onAdd={() => this.props.mutateParentState(s => {
                                    s.currentPopup = ImmunizationProfileInteractionPopupType.ImmunityProof;
                                    s.formMode = FormModificationState.Creating;
                                    s.currentCondition = null;
                                })}
                                onActionClick={(item, isReadOnly) => this.props.mutateParentState(s => {
                                    s.currentCondition = item;
                                    s.currentPopup = ImmunizationProfileInteractionPopupType.ImmunityProof;
                                    s.formMode = isReadOnly ? FormModificationState.Viewing : FormModificationState.Editing;
                                })}
                            />
                        )}
                        {this.renderRow(
                            <ImmunizationRefusalTable
                                isReadOnly={state.isProfileReadOnly}
                                dataSource={this.toDataSource(immunizationProfileModel?.VaccinationRefusals
                                    ? [
                                        ...immunizationProfileModel.VaccinationRefusals
                                            .filter(x => !x.DatePeriod?.End)
                                            .sortByPropertyDesc(x => x.DatePeriod?.Start),
                                        ...immunizationProfileModel.VaccinationRefusals
                                            .filter(x => !!x.DatePeriod?.End)
                                            .sortByPropertyDesc(x => x.DatePeriod?.End)
                                    ]
                                    : []
                                )}
                                onAdd={() => this.props.mutateParentState(
                                    s => {
                                        s.currentPopup = ImmunizationProfileInteractionPopupType.ImmunizationRefusal;
                                        s.formMode = FormModificationState.Creating;
                                        s.currentVaccinationRefusal = null;
                                    }
                                )}
                                onEndClick={vaccinationRefusal => this.props.mutateParentState(s => {
                                    const index = s.immunizationProfileModel.VaccinationRefusals.indexOfObject("Id", vaccinationRefusal.Id);
                                    if (index >= 0)
                                        s.immunizationProfileModel.VaccinationRefusals[index] = vaccinationRefusal;
                                })}
                                onViewClick={vaccinationRefusal =>
                                    this.props.mutateParentState(s => {
                                        s.currentPopup = ImmunizationProfileInteractionPopupType.ImmunizationRefusal;
                                        s.formMode = FormModificationState.Viewing;
                                        s.currentVaccinationRefusal = vaccinationRefusal;
                                    })
                                }
                            />
                        )}
                    </div>

                    <div className="popup-footer">
                        <div className="row">
                            <div className="col-lg-9 col-md-9 col-sm-9">
                                {this.props.parentState.isLoading && <img src={img_loading} alt="loading" />}
                            </div>
                            <div className="col-lg-3 col-md-3 col-sm-3 text-right">
                                <div className="flex-container justifyContent-end">
                                    <button
                                        className="btn btn-default push-right-5"
                                        onClick={() => this.props.onClose()}
                                    >
                                        {cacheStore.GetString("CancelLabel")}
                                    </button>
                                    <button
                                        className="btn btn-primary push-right-5"
                                        onClick={() => this.onPrint()}
                                        disabled={!this.immunizationProfileIsLoaded}
                                    >
                                        {cacheStore.GetString("PrintLabel")}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    private renderRow(element: JSX.Element): JSX.Element {
        return (
            <div className="row">
                <div className="col-md-12" style={{ marginBottom: "20px" }}>
                    {element}
                </div>
            </div>
        );
    }

    private get hasInnerPopup(): boolean {
        return this.props.parentState.currentPopup !== ImmunizationProfileInteractionPopupType.None;
    }

    private closePopups(): void {
        this.props.mutateParentState(s => {
            s.currentPopup = ImmunizationProfileInteractionPopupType.None;
            s.formMode = FormModificationState.None;
        });
    }

    private onSave(): void {
        throw new Error("Not implemented");
    }

    private openHistoryVacinationModelPopup(model: ImmunizationModel, popupType: ImmunizationProfileInteractionPopupType, formMode: FormModificationState): void {
        this.props.mutateParentState(s => {
            s.currentPopup = popupType;
            s.currentImmunization = model;
            s.formMode = formMode;
            if (popupType === ImmunizationProfileInteractionPopupType.ViewVaccination)
                this.selectMedicalItemImmunization(model);
        });
    }

    private selectMedicalItemImmunization(model: ImmunizationModel): void {
        sessionStore.dispatch(LocalPatientOperations.MedicalNote.SelectMedicalImmunizationFromRvq, { immunizationModel: model });
    }

    private renderPopup(): JSX.Element {
        const commonProps = {
            formModificationMode: this.props.parentState.formMode,
            onClose: () => this.closePopups(),
            onSave: () => this.onSave(),
            patientId: this.props?.immunizationProfileModel?.Patient?.Id,
            locationPreferences: this.props.locationPreferences,
            updatePreferences: (medicationPreferences: RvqMedicationPreference[], practitionerPreferences: RvqPractitionerPreference[], locationPreferences: RvqLocationPreference[], then: () => void) => this.props.onUpdatePreferences(medicationPreferences, practitionerPreferences, locationPreferences, then)
        };

        switch (this.props.parentState.currentPopup) {
            case ImmunizationProfileInteractionPopupType.ImmunizationRefusal:
                return (
                    <ImmunizationRefusal
                        {...commonProps}
                        patientDateOfBirth={this.props.parentState?.immunizationProfileModel?.Patient?.Birthdate}
                        width={700}
                        id="VaccinationRefusalPopup"
                        title={cacheStore.GetString("QuebecVaccinationRegistry_VaccinationRefusalLabel")}
                        addVaccinationRefusals={model => this.addVaccinationRefusals(model)}
                        currentVaccinationRefusal={this.props.parentState.currentVaccinationRefusal}
                    />
                );

            case ImmunizationProfileInteractionPopupType.OverrideVaccinationStatus:
                return (
                    <VaccinationOverrideStatusComponent
                        {...commonProps}
                        width={700}
                        id="VaccinationOverrideStatusPopup"
                        title={QuebecVaccinationRegistryHelper.isResetStatusAction(this.props.parentState.currentImmunization.OverrideStatus)
                            ? cacheStore.GetString("QuebecVaccinationRegistry_ResetVaccinationActStatusLabel")
                            : cacheStore.GetString("QuebecVaccinationRegistry_OverrideVaccinationActStatusLabel")
                        }
                        immunization={this.props.parentState.currentImmunization}
                        updateImmunizationModel={model => this.updateImmunizationModel(model)}
                    />
                );

            case ImmunizationProfileInteractionPopupType.ImmunityProof:
                return (
                    <AntigenCommonComponent
                        {...commonProps}
                        width={600}
                        height={this.props.parentState.formMode === FormModificationState.Creating ? 555 : null}
                        id="ImmunityProofPopup"
                        title={cacheStore.GetString("QuebecVaccinationRegistry_ProofOfImmunityLabel")}
                        category={ConditionCategory.immunizationProof}
                        conditionModel={this.props.parentState.currentCondition}
                        updateConditionModel={(model, appliedLocation) => this.updateConditionModel(model, appliedLocation)}
                    />
                );

            case ImmunizationProfileInteractionPopupType.CounterindicationOrPrecaution:
                return (
                    <AntigenCommonComponent
                        {...commonProps}
                        width={600}
                        height={this.props.parentState.formMode === FormModificationState.Creating ? 700 : null}
                        top={75}
                        id="CounterindicationOrPrecautionPopup"
                        title={cacheStore.GetString("QuebecVaccinationRegistry_ContraindicationPrecautionLabel")}
                        category={ConditionCategory.contraindicationPrecaution}
                        conditionModel={this.props.parentState.currentCondition}
                        updateConditionModel={(model, appliedLocation) => this.updateConditionModel(model, appliedLocation)}
                    />
                );

            case ImmunizationProfileInteractionPopupType.Preferences:
                return (
                    <ImmunizationPreferences
                        {...commonProps}
                        top={100}
                        width={600}
                        height={700}
                        formModificationMode={FormModificationState.Editing}
                        id="PreferencesPopup"
                        title={cacheStore.GetString("QuebecVaccinationRegistry_PreferencesLabel")}
                        currentMedicationPreferences={this.props.medicationPreferences}
                        currentPractitionerPreferences={this.props.practitionerPreferences}
                        currentLocationPreferences={this.props.locationPreferences}
                        updatePreferences={(preferences, practitionerPreferences, locationPreferences) => {
                            this.props.onUpdatePreferences(preferences, practitionerPreferences, locationPreferences);
                            this.props.mutateParentState(s => s.currentPopup = ImmunizationProfileInteractionPopupType.None);
                        }}
                    />
                );

            case ImmunizationProfileInteractionPopupType.ImmunityDeletion:
                return (
                    <ImmunizationDeletion
                        {...commonProps}
                        width={600}
                        id="VaccinationDeletion"
                        onVaccinationRemoval={(medicalItemId, deletionDate) => this.props.mutateParentState(
                            s => {
                                s.immunizationProfileModel.Immunizations = s.immunizationProfileModel.Immunizations.filter(i => i.Id !== s.currentImmunization.Id);
                                s.currentImmunization = null;
                                s.currentPopup = ImmunizationProfileInteractionPopupType.None;
                            },
                            () => medicalItemId && deletionDate && sessionStore.dispatch(
                                LocalPatientOperations.MedicalNote.UpdateImmunizationMedicalItem,
                                {
                                    immunization: new MedicalItemImmunization({
                                        Id: medicalItemId,
                                        QuebecRegistryDeletionDate: deletionDate,
                                        QuebecRegistryId: null
                                    })
                                }
                            )
                        )}
                        title={cacheStore.GetString("QuebecVaccinationRegistry_VaccinationActDeletionTitle")}
                        immunization={this.props.parentState.currentImmunization}
                        updateImmunizationModel={model => this.updateImmunizationModel(model)}
                    />
                );

            case ImmunizationProfileInteractionPopupType.ViewVaccination:
                return (
                    <ViewImmunization
                        {...commonProps}
                        width={700}
                        id="ViewImmunizationAct"
                        title={cacheStore.GetString("QuebecVaccinationRegistry_VaccinationInformationLabel")}
                        immunization={this.props.parentState.currentImmunization}
                        medicalImmunizatioModel={this.props.model.get("SelectedMedicalImmunizationFromRvq")}
                        modifyImmunization={() => {
                            sessionStore.dispatch(
                                LocalPatientOperations.MedicalItems.SelectEMRTemplate,
                                {
                                    value: this.props.model.get("SelectedMedicalImmunizationFromRvq").EMRTemplateId
                                }
                            );
                            sessionStore.dispatch(LocalPatientOperations.MedicalItems.Modify);
                            this.props.onClose();
                        }}
                    />
                );

            default:
                return null;
        }
    }

    private toDataSource<T>(values: T[]): TableDataSourceItem<T>[] {
        return values?.map(item => new TableDataSourceItem(item));
    }

    private async onPrint(): Promise<void> {
        const response = await Client.postAsync<GenerateImmunizationProfilePdfResponse>(
            ServerOperations.Server.QuebecVaccinationRegistry_GenerateImmunizationProfilePdf,
            {
                PatientId: this.props.immunizationProfileModel.Patient.Id
            }
        );

        if (response.isSuccess)
            PrintHelper.ShowPrintPreviewFileName(response.data.FileName);

        else
            sessionStore.showPopup([
                cacheStore.GetString("QuebecVaccinationRegistry_FailedToPrintImmunizationProfile"),
                ...response.errors
            ]);
    }

    private get immunizationProfileIsLoaded(): boolean {
        return !!this.props.immunizationProfileModel;
    }

    private addVaccinationRefusals(models: VaccinationRefusalModel[]): Promise<void> {
        if (!models.any())
            return;

        const immunization = this.props.immunizationProfileModel;

        immunization.VaccinationRefusals = [
            ...immunization.VaccinationRefusals,
            ...models
        ];

        this.props.updateImmunizationProfileModel(immunization);
    }

    private updateConditionModel(model: ConditionModel, appliedLocation?: RvqLocationPreference): void {
        if (!model)
            return;

        let immunization = this.props.immunizationProfileModel;
        if (this.props.parentState.formMode !== FormModificationState.Creating)
            immunization.Conditions = immunization.Conditions.filter(x => x.Id !== model.Id);
        immunization.Conditions?.push(model);

        this.props.updateImmunizationProfileModel(immunization, appliedLocation);
    }

    private updateImmunizationModel(model: ImmunizationModel): void {
        if (!model)
            return;

        let immunization = this.props.immunizationProfileModel;
        immunization.Immunizations = immunization.Immunizations.filter(x => x.Id !== model.Id);
        immunization.Immunizations.push(model);

        this.props.updateImmunizationProfileModel(immunization);
    }

    private renderPlannedVaccinationStatus(statusKey: string, descriptionKey: string): JSX.Element {
        return (
            <div className="row">
                <div className="col-xs-1">
                    {cacheStore.GetString(statusKey)}:
                </div>
                <div className="col-xs-11">
                    {cacheStore.GetString(descriptionKey)}
                </div>
            </div>
        );
    }
}

export { ImmunizationProfileComponent };
